import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 人工智能 - 横屏「深度学习神经网络」风格
 *
 * 核心亮点：
 * - SVG 神经网络可视化（3 层：输入 4 节点、隐藏 6 节点、输出 3 节点）
 * - 动画连接线与数据流粒子
 * - 训练指标面板（准确率、损失值等动画计数器）
 * - 概念解释卡片（反向传播、激活函数、梯度下降等）
 * - 模型架构描述
 *
 * 尺寸：1920x1080 横屏
 */

interface LayerInfo {
  name: string;
  nodes: number;
  color: string;
}

interface MetricInfo {
  label: string;
  value: string;
  icon: string;
  color: string;
}

interface ConceptInfo {
  title: string;
  description: string;
  icon: string;
}

interface AINetworkTemplateProps {
  topic: string;
  subtitle: string;
  layers: LayerInfo[];
  metrics: MetricInfo[];
  concepts: ConceptInfo[];
  keyFact: string;
  applications: string[];
}

const DEFAULT_PROPS: AINetworkTemplateProps = {
  topic: '深度学习与神经网络',
  subtitle: 'Deep Learning & Neural Networks',
  layers: [
    { name: '输入层', nodes: 4, color: '#06b6d4' },
    { name: '隐藏层', nodes: 6, color: '#818cf8' },
    { name: '输出层', nodes: 3, color: '#a78bfa' },
  ],
  metrics: [
    { label: '训练准确率', value: '98.7%', icon: '📈', color: '#06b6d4' },
    { label: '损失值', value: '0.013', icon: '📉', color: '#818cf8' },
    { label: '训练轮次', value: '50', icon: '🔄', color: '#a78bfa' },
    { label: '学习率', value: '0.001', icon: '⚡', color: '#06b6d4' },
  ],
  concepts: [
    {
      title: '反向传播',
      description: '通过链式法则将误差从输出层向输入层传播，更新各层权重',
      icon: '↩️',
    },
    {
      title: '激活函数',
      description: 'ReLU、Sigmoid 等非线性函数，赋予网络学习复杂模式的能力',
      icon: '⚡',
    },
    {
      title: '梯度下降',
      description: '沿损失函数梯度反方向更新参数，逐步逼近最优解',
      icon: '📐',
    },
  ],
  keyFact: '人脑约有 860 亿个神经元，而 GPT-4 参数量达 1.7 万亿，但能耗仅为大脑的百万分之一',
  applications: [
    '图像识别与分类',
    '自然语言处理',
    '自动驾驶',
    '医疗诊断',
    '推荐系统',
  ],
};

/** 解析数值字符串用于动画计数 */
function parseValueForCounter(value: string): { num: number; suffix: string; decimals: number } {
  const pct = value.match(/^([\d.]+)%$/);
  if (pct) return { num: parseFloat(pct[1]), suffix: '%', decimals: 1 };
  const num = parseFloat(value);
  if (!isNaN(num)) {
    const parts = value.split('.');
    const decimals = parts[1]?.length ?? 0;
    return { num, suffix: '', decimals };
  }
  return { num: 0, suffix: '', decimals: 0 };
}

const AnimatedMetricValue: React.FC<{
  value: string;
  frame: number;
  start: number;
}> = ({ value, frame, start }) => {
  const { num, suffix, decimals } = parseValueForCounter(value);
  const progress = interpolate(frame, [start, start + 35], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const eased = 1 - Math.pow(1 - progress, 3);
  const displayNum = num * eased;
  const formatted =
    decimals > 0 ? displayNum.toFixed(decimals) : Math.floor(displayNum).toString();
  return (
    <>
      {formatted}
      {suffix}
    </>
  );
};

export const AINetworkTemplate: React.FC<Partial<AINetworkTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const svgWidth = 900;
  const svgHeight = 380;
  const svgLabelH = 50;
  const svgX = 480;
  const svgY = 120;

  const layerPositions = [
    { x: 120, nodes: p.layers[0]?.nodes ?? 4 },
    { x: 450, nodes: p.layers[1]?.nodes ?? 6 },
    { x: 780, nodes: p.layers[2]?.nodes ?? 3 },
  ];

  const getNodePositions = (x: number, count: number) => {
    const centerY = svgHeight / 2;
    const spacing = Math.min(50, (svgHeight - 60) / (count + 1));
    const startY = centerY - ((count - 1) * spacing) / 2;
    return Array.from({ length: count }, (_, i) => ({
      x,
      y: startY + i * spacing,
    }));
  };

  const inputNodes = getNodePositions(layerPositions[0].x, layerPositions[0].nodes);
  const hiddenNodes = getNodePositions(layerPositions[1].x, layerPositions[1].nodes);
  const outputNodes = getNodePositions(layerPositions[2].x, layerPositions[2].nodes);

  // Animation phases
  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });
  const networkDraw = interpolate(frame, [25, 120], [0, 1], { extrapolateRight: 'clamp' });
  const particlePhase = (frame * 0.5) % 1;

  const metricsStart = 130;
  const conceptsStart = metricsStart + 60;
  const keyFactStart = conceptsStart + p.concepts.length * 35 + 30;
  const keyFactOpacity = interpolate(frame, [keyFactStart, keyFactStart + 25], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const applicationsStart = keyFactStart + 50;

  const colors = [p.layers[0]?.color ?? '#06b6d4', p.layers[1]?.color ?? '#818cf8', p.layers[2]?.color ?? '#a78bfa'];

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 50%, #0a0a1a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* 网格背景 - 科技感 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(6,182,212,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(6,182,212,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* 矩阵风格粒子 */}
      {Array.from({ length: 30 }).map((_, i) => {
        const x = (i * 97) % 1920;
        const y = ((i * 73 + frame * 0.5) % 1080);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: 2,
              height: 2,
              borderRadius: '50%',
              background: '#06b6d4',
              opacity: 0.08,
            }}
          />
        );
      })}

      {/* 标题区 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 60,
          right: 60,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          opacity: headerProgress,
          transform: `translateY(${(1 - headerProgress) * -10}px)`,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 18px',
            background: 'rgba(6,182,212,0.1)',
            border: '1px solid rgba(6,182,212,0.3)',
            borderRadius: 10,
            fontSize: 26,
            color: '#06b6d4',
            fontWeight: 700,
            letterSpacing: '0.12em',
            whiteSpace: 'nowrap',
          }}
        >
          🤖 AI / 人工智能
        </div>
        <h1 style={{ fontSize: 42, fontWeight: 900, margin: 0, whiteSpace: 'nowrap' }}>{p.topic}</h1>
        <p style={{ fontSize: 28, color: 'rgba(255,255,255,0.4)', margin: 0, whiteSpace: 'nowrap' }}>{p.subtitle}</p>
      </div>

      {/* ========== SVG 神经网络可视化 ========== */}
      <svg
        style={{ position: 'absolute', left: svgX, top: svgY }}
        width={svgWidth}
        height={svgHeight + svgLabelH}
        viewBox={`0 0 ${svgWidth} ${svgHeight + svgLabelH}`}
      >
        {/* 连接线：输入 -> 隐藏 */}
        {inputNodes.map((inNode, i) =>
          hiddenNodes.map((hidNode, j) => {
            const pathProgress = interpolate(
              networkDraw,
              [(i * hiddenNodes.length + j) / (inputNodes.length * hiddenNodes.length), 1],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
            );
            const pathLen = Math.hypot(hidNode.x - inNode.x, hidNode.y - inNode.y);
            const dashOffset = pathLen - (particlePhase * pathLen * 2);
            return (
              <g key={`ih-${i}-${j}`} opacity={pathProgress}>
                <line
                  x1={inNode.x}
                  y1={inNode.y}
                  x2={hidNode.x}
                  y2={hidNode.y}
                  stroke={colors[0]}
                  strokeWidth="1"
                  opacity="0.15"
                />
                <line
                  x1={inNode.x}
                  y1={inNode.y}
                  x2={hidNode.x}
                  y2={hidNode.y}
                  stroke={colors[0]}
                  strokeWidth="1.5"
                  opacity="0.4"
                  strokeDasharray="8 6"
                  strokeDashoffset={-dashOffset}
                />
              </g>
            );
          })
        )}

        {/* 连接线：隐藏 -> 输出 */}
        {hiddenNodes.map((hidNode, i) =>
          outputNodes.map((outNode, j) => {
            const pathProgress = interpolate(
              networkDraw,
              [0.5 + (i * outputNodes.length + j) / (hiddenNodes.length * outputNodes.length) * 0.5, 1],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
            );
            const pathLen = Math.hypot(outNode.x - hidNode.x, outNode.y - hidNode.y);
            const dashOffset = pathLen - (particlePhase * pathLen * 2);
            return (
              <g key={`ho-${i}-${j}`} opacity={pathProgress}>
                <line
                  x1={hidNode.x}
                  y1={hidNode.y}
                  x2={outNode.x}
                  y2={outNode.y}
                  stroke={colors[1]}
                  strokeWidth="1"
                  opacity="0.15"
                />
                <line
                  x1={hidNode.x}
                  y1={hidNode.y}
                  x2={outNode.x}
                  y2={outNode.y}
                  stroke={colors[1]}
                  strokeWidth="1.5"
                  opacity="0.4"
                  strokeDasharray="8 6"
                  strokeDashoffset={-dashOffset}
                />
              </g>
            );
          })
        )}

        {/* 数据流粒子 */}
        {networkDraw > 0.3 &&
          [0, 1, 2].map((idx) => {
            const t = (particlePhase + idx * 0.33) % 1;
            const inN = inputNodes[Math.floor(t * inputNodes.length) % inputNodes.length];
            const hidN = hiddenNodes[Math.floor(t * hiddenNodes.length) % hiddenNodes.length];
            const midX = inN.x + (hidN.x - inN.x) * t;
            const midY = inN.y + (hidN.y - inN.y) * t;
            return (
              <circle
                key={`p1-${idx}`}
                cx={midX}
                cy={midY}
                r={3}
                fill="#06b6d4"
                opacity={0.6}
              />
            );
          })}
        {networkDraw > 0.5 &&
          [0, 1, 2].map((idx) => {
            const t = (particlePhase + idx * 0.33) % 1;
            const hidN = hiddenNodes[Math.floor(t * hiddenNodes.length) % hiddenNodes.length];
            const outN = outputNodes[Math.floor(t * outputNodes.length) % outputNodes.length];
            const midX = hidN.x + (outN.x - hidN.x) * t;
            const midY = hidN.y + (outN.y - hidN.y) * t;
            return (
              <circle
                key={`p2-${idx}`}
                cx={midX}
                cy={midY}
                r={3}
                fill="#818cf8"
                opacity={0.6}
              />
            );
          })}

        {/* 输入层节点 */}
        {inputNodes.map((pos, i) => {
          const nodeProgress = interpolate(networkDraw, [i * 0.08, 0.4], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <g key={`in-${i}`} opacity={nodeProgress}>
              <circle
                cx={pos.x}
                cy={pos.y}
                r={14}
                fill={colors[0]}
                opacity={0.8}
              />
              <circle
                cx={pos.x}
                cy={pos.y}
                r={18}
                fill="none"
                stroke={colors[0]}
                strokeWidth="2"
                opacity={0.4}
              />
            </g>
          );
        })}

        {/* 隐藏层节点 */}
        {hiddenNodes.map((pos, i) => {
          const nodeProgress = interpolate(networkDraw, [0.3 + i * 0.05, 0.7], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <g key={`hid-${i}`} opacity={nodeProgress}>
              <circle cx={pos.x} cy={pos.y} r={14} fill={colors[1]} opacity={0.8} />
              <circle
                cx={pos.x}
                cy={pos.y}
                r={18}
                fill="none"
                stroke={colors[1]}
                strokeWidth="2"
                opacity={0.4}
              />
            </g>
          );
        })}

        {/* 输出层节点 */}
        {outputNodes.map((pos, i) => {
          const outStart = Math.min(0.6 + i * 0.1, 0.9);
          const nodeProgress = interpolate(networkDraw, [outStart, outStart + 0.1], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <g key={`out-${i}`} opacity={nodeProgress}>
              <circle cx={pos.x} cy={pos.y} r={14} fill={colors[2]} opacity={0.8} />
              <circle
                cx={pos.x}
                cy={pos.y}
                r={18}
                fill="none"
                stroke={colors[2]}
                strokeWidth="2"
                opacity={0.4}
              />
            </g>
          );
        })}

        {/* 层标签 */}
        {p.layers.map((layer, i) => {
          const lx = layerPositions[i]?.x ?? 0;
          const ly = svgHeight + 18;
          const labelStart = Math.min(i * 0.2 + 0.4, 0.9);
          const labelProgress = interpolate(networkDraw, [labelStart, labelStart + 0.1], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <g key={`label-${i}`} opacity={labelProgress}>
              <text
                x={lx}
                y={ly}
                textAnchor="middle"
                fill={colors[i]}
                fontSize="24"
                fontWeight="700"
              >
                {layer.name}
              </text>
              <text
                x={lx}
                y={ly + 30}
                textAnchor="middle"
                fill="rgba(255,255,255,0.4)"
                fontSize="22"
              >
                {layer.nodes} 节点
              </text>
            </g>
          );
        })}
      </svg>

      {/* ========== 训练指标面板 ========== */}
      <div
        style={{
          position: 'absolute',
          top: 600,
          left: 60,
          width: 330,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: 'rgba(255,255,255,0.4)',
            marginBottom: 14,
            fontWeight: 600,
            letterSpacing: '0.08em',
          }}
        >
          📊 训练指标
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.metrics.map((m, i) => {
            const start = metricsStart + i * 18;
            const progress = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 12, stiffness: 140 },
            });
            return (
              <div
                key={i}
                style={{
                  padding: '10px 14px',
                  background: `${m.color}08`,
                  borderRadius: 12,
                  border: `1px solid ${m.color}25`,
                  opacity: progress,
                  transform: `translateX(${(1 - progress) * -20}px)`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span style={{ fontSize: 22 }}>{m.icon}</span>
                <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.5)' }}>{m.label}</span>
                <span
                  style={{
                    fontSize: 26,
                    fontWeight: 800,
                    color: m.color,
                    fontVariantNumeric: 'tabular-nums',
                    marginLeft: 'auto',
                  }}
                >
                  <AnimatedMetricValue value={m.value} frame={frame} start={start} />
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== 概念解释卡片 ========== */}
      <div
        style={{
          position: 'absolute',
          top: 600,
          left: 510,
          right: 440,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: 'rgba(255,255,255,0.4)',
            marginBottom: 14,
            fontWeight: 600,
            letterSpacing: '0.08em',
          }}
        >
          📚 核心概念
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {p.concepts.map((c, i) => {
            const start = conceptsStart + i * 35;
            const progress = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  flex: '1 1 200px',
                  padding: '16px 20px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: 14,
                  border: '1px solid rgba(129,140,248,0.2)',
                  opacity: progress,
                  transform: `translateY(${(1 - progress) * 12}px)`,
                }}
              >
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: '#818cf8',
                    marginBottom: 8,
                  }}
                >
                  {c.icon} {c.title}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    color: 'rgba(255,255,255,0.65)',
                    lineHeight: 1.5,
                  }}
                >
                  {c.description}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== 应用场景 ========== */}
      <div
        style={{
          position: 'absolute',
          top: 600,
          right: -80,
          width: 360,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: 'rgba(255,255,255,0.4)',
            marginBottom: 14,
            fontWeight: 600,
            letterSpacing: '0.08em',
          }}
        >
          🎯 典型应用
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {p.applications.map((app, i) => {
            const start = applicationsStart + i * 22;
            const opacity = interpolate(frame, [start, start + 18], [0, 1], {
              extrapolateRight: 'clamp',
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  opacity,
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: '#06b6d4',
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
                  {app}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== 关键事实 ========== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 60,
          right: 60,
          opacity: keyFactOpacity,
        }}
      >
        <div
          style={{
            padding: '18px 28px',
            background: '#0b1a22',
            border: '1px solid rgba(6,182,212,0.2)',
            borderRadius: 16,
            textAlign: 'center',
          }}
        >
          <span
            style={{
              fontSize: 22,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.6,
            }}
          >
            🌟 {p.keyFact}
          </span>
        </div>
      </div>

      {/* 进度条 */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 450], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #06b6d4, #818cf8, #a78bfa)',
        }}
      />
    </AbsoluteFill>
  );
};
